import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, NumberInput } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, LatLng, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory, ObjectStorageFileType } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import FetchingSelect from '@/components/FetchingSelect';
import { DiscoveryPoint, UpsertDiscoveryPoint } from '@/models/discovery-point.model';
import { upsertDiscoveryPoint } from '@/services/discovery-point.service';
import { getCitiesLookup, getCity } from '@/services/city.service';
import MapMultipleSelector, { MarkerInfo } from '@/components/MapMultipleSelector';
import FileUploader from '@/components/FileUploader';
import { City } from '@/models/city.model';
import { hk_lat_lng } from '@/constants/map';

const schema = Schema.Model<UpsertDiscoveryPoint>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  imageUrl: Schema.Types.StringType(),
  objUrl: Schema.Types.StringType(),
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  lat: Schema.Types.NumberType().isRequired('This field is required.'),
  lon: Schema.Types.NumberType().isRequired('This field is required.'),
  radius: Schema.Types.NumberType().isRequired('This field is required.'),
});


const emptyModel: UpsertDiscoveryPoint = {
  id: undefined,
  name: '',
  imageUrl: '',
  objUrl: '',
  cityId: undefined,
  lat: undefined,
  lon: undefined,
  radius: undefined,
};

interface DiscoveryPointFormProps{
  city?: City,
  formObject?: DiscoveryPoint;
  pageState: PageState
};

const DiscoveryPointForm: React.FC<DiscoveryPointFormProps> = ({formObject, city, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertDiscoveryPoint>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<LatLng>();
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, cityId: city?.id});
      if(city){
        setMapCenter({lat: city?.lat, lng: city?.lon});
      }      
      return;
    }

    if (formObject) {
      const castedModel: UpsertDiscoveryPoint = { 
        ...formObject, 
      };
      setFormValue(castedModel);
      updateSelectedCity(formObject.cityId);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject, city]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertDiscoveryPoint(formValue);
      if (result.success) {
        if(city){
          nav(`/cities/${formValue.cityId}`, {state:{tab: "discovery-points"}});
        }else{
          nav(`/discovery-points`);
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };
  const updateSelectedCity = async(cityId: number|undefined) => {
    // console.log('city changed:', cityId);
    if(cityId){
      getCity(cityId).then(result => {
        setMapCenter({lat: result.result?.lat, lng: result.result?.lon});
      });
      return;
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  const getMapValues = (): MarkerInfo[] => {
    const values : MarkerInfo[] = [];
    if(formValue.lat && formValue.lon){
      values.push({
        key:'discovery-point', 
        radius: Number(formValue.radius), 
        position:{
          lat: Number(formValue.lat), 
          lng: Number(formValue.lon)
        }
      });
    }
    return values;
  };

  const handleOnMapClick = (position: LatLng) => {
    if(!formValue.lat || !formValue.lon){
      setFormValue(pre => ({...pre, lat: position.lat, lon: position.lng}));
    }
  };

  const handleMarkerDrag = (key: string, position: LatLng) => {
    if(key == 'discovery-point'){
      setFormValue(pre => ({...pre, lat: position.lat, lon: position.lng}));
    }
  };

  return (
    <PageContent>
      <Form name='upsert-discovery-point' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={schema}>
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name"/>
              </Form.Group>
              <Form.Group>
                <Form.Label>City</Form.Label>
                <Form.Control name="cityId" 
                  accepter={FetchingSelect} block
                  fetchData={(text, page) => getCitiesLookup(text, undefined, page)} 
                  placeholder="Select a city"
                  disabled={!!city}
                  onChange={val => updateSelectedCity(val)}
                />
              </Form.Group>
              <Row>
                <Col span={{sm:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Latitude</Form.Label>
                      <Form.Control name="lat" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
                <Col span={{sm:24, xl:12}}>
                    <Form.Group>
                      <Form.Label>Longitude</Form.Label>
                      <Form.Control name="lon" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Radius</Form.Label>
                <Form.Control name="radius" accepter={NumberInput}/>
              </Form.Group>
              <Form.Group>
                <Form.Label>Upload 3D Object</Form.Label>
                <FileUploader 
                  category={ObjectStorageFileCategory.DiscoveryPointObject} 
                  type={ObjectStorageFileType.Other}
                  objectUrl={formValue?.objUrl}
                  onChange={url => setFormValue(pre => ({...pre, objUrl: url}))}
                />
              </Form.Group>     
              <Form.Group>           
                <Form.Label style={{ textAlign: 'center'}}>Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="imageUrl"
                  height="200px"
                  url={formValue.imageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.DiscoveryPointImage}
                  onChange={url => setFormValue(pre => ({...pre, imageUrl: url}))}              
                />
              </Form.Group>
            </Col>
            <Col span={{xs:24, xl:16}}>
              <Form.Group>
                <Form.Label style={{ textAlign: 'center'}}>{
                  (!formValue.lat || !formValue.lon) ? 
                  'Click on map to select Discovery point' : 
                  'Drag Marker to update Discovery point'
                }</Form.Label>
                <MapMultipleSelector
                  value={getMapValues()}
                  onClick={handleOnMapClick}
                  onMarkerDrag={handleMarkerDrag}
                  height='700px'
                  defaultZoom={15}
                  defaultCenter={mapCenter || hk_lat_lng}
                />                  
              </Form.Group>
            </Col>
            {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
          </Row>
        </Grid>
      </Form>
      <Divider />
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default DiscoveryPointForm;

